import { Box, Button, Grid2 } from "@mui/material";
import { StateEnum, StepsEnum } from "./HorizontalLinearStepper";
import { LoadingIndicator } from "../../Common/UI/components/Loading";
import useFetchMsal from "../../Common/UI/hooks/useFetchMsal";
import { useState } from "react";
import SNEvent from "./interfaces/SNEvent";
import RedButton from "../../Common/UI/components/RedButton";
import { config } from "../../authConfig";
import useURLBuilder from "../../Common/UI/hooks/useURLBuilder";
import useIsAdminAndViewOnly from "../../Common/UI/hooks/useIsAdminAndViewOnly";
import { CommentDialog } from "./components/CommentDialog";

export default function CompletedStep({
  handleStep,
  event,
  setEvent,
  setReportTotal,
  setValue,
}: {
  event: SNEvent;
  handleStep: (stepNum: number) => void;
  setEvent: React.Dispatch<React.SetStateAction<SNEvent>>;
  setReportTotal: React.Dispatch<React.SetStateAction<number | null>>;
  setValue: React.Dispatch<React.SetStateAction<number>>;
}) {
  const execute = useFetchMsal();
  const { isViewOnly, isAdmin } = useIsAdminAndViewOnly();
  const urlBuilder = useURLBuilder();
  const [isSending, setSending] = useState<boolean>(false);
  const [showCommentPopup, setShowCommentPopup] = useState<boolean>(false);

  const handleCloseComments = () => setShowCommentPopup(false);
  const handleArchive = async function (comment: string) {
    try {
      setSending(true);
      setEvent((prevEvent) => {
        prevEvent.comment = comment;
        return prevEvent;
      });
      const url = `${config.rootAPIUrl}${config.rootAPIList?.archive}`;
      const response = await execute("POST", url, event);
      setEvent(response);
      setSending(false);
      handleStep(StepsEnum.New);
    } catch (error) {
      console.error(error);
      throw new Error("Event archival failed!");
    }
  };

  const handleSendForAccountingApproval = async function () {
    try {
      setSending(true);
      const url = urlBuilder(`${config.rootAPIUrl}${config.rootAPIList?.accountingApproval}`, {
        id: event.id!,
      });

      const response = await execute("POST", url);
      setEvent(response);
      setSending(false);
      handleStep(StepsEnum.AccountingApproval);
      setValue(1);
    } catch (error) {
      console.error(error);
      throw new Error("Event send for accounting approval has failed!");
    }
  };

  const handleUpdateContribution = async function () {
    try {
      setSending(true);
      const url = urlBuilder(`${config.rootAPIUrl}${config.rootAPIList?.contributionUpdate}`, {
        id: event.id!,
        addlDonation: parseFloat(event.donationContribution?.toString() || "0"),
        addlSales: parseFloat(event.salesContribution?.toString() || "0"),
      });

      const response = await execute("POST", url);
      setEvent(response);
      setSending(false);
      handleStep(StepsEnum.AccountingApproval);
      setValue(1);
    } catch (error) {
      console.error(error);
      throw new Error("Event contribution update has failed!");
    }
  };

  const handleGenerateReport = async function () {
    try {
      setSending(true);
      const url = urlBuilder(`${config.rootAPIUrl}${config.rootAPIList?.generateReport}`, {
        id: event.id!,
      });
      const response = await execute("GET", url);
      setReportTotal(response.total);
      setSending(false);
    } catch (error) {
      console.error(error);
      throw new Error("Calculate Donation has failed!");
    }
  };

  return (
    <LoadingIndicator show={isSending}>
      {event.stateId === StateEnum.Completed && !isViewOnly && (
        <Box>
          <RedButton
            text="Calculate Donation"
            disabled={false}
            onClick={async () => {
              await handleGenerateReport();
              await handleSendForAccountingApproval();
            }}
            style={{ margin: "10px" }}
          ></RedButton>
        </Box>
      )}

      {event.stateId === StateEnum.PendingContributionCorrection && !isViewOnly && (
        <Box>
          <RedButton
            text="Update Contribution"
            disabled={false}
            onClick={async () => {
              await handleUpdateContribution();
            }}
            style={{ margin: "10px" }}
          ></RedButton>
        </Box>
      )}

      {isAdmin && (
        <Button variant="outlined" onClick={() => setShowCommentPopup(true)} sx={{ marginLeft: 2 }}>
          Archive
        </Button>
      )}

      {showCommentPopup ? <CommentDialog handleCloseComments={handleCloseComments} handleAction={handleArchive} /> : <></>}
    </LoadingIndicator>
  );
}
